import * as React from 'react';

/**
 * aurora-mail と同じ `data-theme` 属性方式。
 * `<html data-theme="aurora">` を切り替えるだけで tokens.css の変数が入れ替わる。
 */
export const THEMES = [
  'light',
  'dark',
  'aurora',
  'midnight',
  'nord',
  'dracula',
  'solarized-dark',
  'solarized-light',
] as const;
export type ThemeName = (typeof THEMES)[number];

export const THEME_LABELS: Record<ThemeName, string> = {
  light: 'ライト',
  dark: 'ダーク',
  aurora: 'オーロラ',
  midnight: 'ミッドナイト',
  nord: 'ノルド',
  dracula: 'ドラキュラ',
  'solarized-dark': 'ソラライズド（ダーク）',
  'solarized-light': 'ソラライズド（ライト）',
};

/**
 * **明色のテーマ**（背景が明るいもの）。
 *
 * Tailwind の `dark:` バリアントと `color-scheme` の判定に使う。
 * 以前は `theme !== 'light'` という**否定形 1 つ**で判定していたので、
 * 明色テーマを 2 つ目（`solarized-light`）に増やした瞬間に
 * 「明るい配色なのに `.dark` が付く」＝ `dark:` 指定のコンポーネントだけが
 * 暗いまま、という壊れ方をする。判定は必ずこの集合を通す。
 */
export const LIGHT_THEMES: readonly ThemeName[] = ['light', 'solarized-light'];

export function isLightTheme(theme: ThemeName): boolean {
  return LIGHT_THEMES.includes(theme);
}

/**
 * 利用者が選べる値。**`system`（OS 追従）を含む点が `THEMES` との違い。**
 *
 * `THEMES` と分けているのは、`system` が「見た目」ではなく「決め方」だから。
 * `data-theme` 属性や `Record<ThemeName, Icon>` のような
 * 「実際に描く見た目」の側に `system` が混ざると、
 * `data-theme="system"` という**トークンの定義が無い値**が HTML に付いて
 * 配色が既定へ落ちる。選択肢（preference）と結果（resolved）を型で分ける。
 */
export const THEME_PREFERENCES = ['system', ...THEMES] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export const THEME_PREFERENCE_LABELS: Record<ThemePreference, string> = {
  system: 'OS の設定に合わせる',
  ...THEME_LABELS,
};

interface ThemeContextValue {
  /** 実際に適用されている見た目。`system` はここには現れない。 */
  theme: ThemeName;
  /** 利用者の選択。`system` を含む。 */
  preference: ThemePreference;
  /**
   * このアプリで**選ばせる**候補。`<ThemeProvider themes={...}>` で絞れる。
   *
   * 全 8 プリセットを常に出す必要は無い（契約者パネルは「OS 追従 / ライト /
   * オーロラ / ミッドナイト」の 4 つで足りる）。選択肢の絞り込みを画面側の
   * 配列リテラルでやると、ピッカーが 2 か所（ヘッダのトグルと設定画面）に
   * あるので必ず片方だけ古くなる。**候補は Provider に 1 本だけ持たせる。**
   */
  preferences: readonly ThemePreference[];
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function isTheme(v: unknown): v is ThemeName {
  return typeof v === 'string' && (THEMES as readonly string[]).includes(v);
}

function isPreference(v: unknown): v is ThemePreference {
  return typeof v === 'string' && (THEME_PREFERENCES as readonly string[]).includes(v);
}

/** OS のダークモード設定。`matchMedia` が無い環境ではライト扱い。 */
function systemTheme(): ThemeName {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolve(preference: ThemePreference): ThemeName {
  return preference === 'system' ? systemTheme() : preference;
}

export function ThemeProvider({
  children,
  storageKey = 'mail-sv.theme',
  defaultTheme = 'aurora',
  themes = THEME_PREFERENCES,
}: {
  children: React.ReactNode;
  storageKey?: string;
  /** 保存された選択が無いときの既定。`system` も渡せる。 */
  defaultTheme?: ThemePreference;
  /** 選ばせる候補。既定は全プリセット + `system`。 */
  themes?: readonly ThemePreference[];
}) {
  /*
   * **保存は 2 本立てにする。**
   *
   *   `<storageKey>`        … 実際に適用した見た目（light / dark / aurora / midnight）
   *   `<storageKey>.pref`   … 利用者の選択（system を含む）
   *
   * `public/theme-init.js`（FOUC 対策として React より前に走る素の JS）は
   * 前者を読んで `data-theme` を当てている。ここに `system` を保存すると、
   * **トークン定義の無い `data-theme="system"` が最初の描画で当たる**。
   * 解決済みの値を従来どおり同じキーに置き続けることで、
   * 各アプリの theme-init.js を 1 行も変えずに OS 追従を足せる。
   */
  const prefKey = `${storageKey}.pref`;

  const [preference, setPreferenceState] = React.useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    try {
      const stored = window.localStorage.getItem(prefKey);
      if (isPreference(stored)) return stored;
      // 旧版（選択の保存が無かった頃）の値を引き継ぐ。
      const legacy = window.localStorage.getItem(storageKey);
      if (isTheme(legacy)) return legacy;
    } catch {
      /* プライベートモード等 */
    }
    return defaultTheme;
  });

  const [theme, setThemeResolved] = React.useState<ThemeName>(() => resolve(preference));

  /*
   * **OS 側の切り替えに追従する。** 追従しないと、`system` を選んでいる利用者は
   * 「夜になって OS がダークに変わったのに画面だけ明るいまま」になり、
   * 選んだ意味が無くなる。`system` 以外を選んでいる間は購読しない。
   */
  React.useEffect(() => {
    if (preference !== 'system') {
      setThemeResolved(preference);
      return;
    }
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setThemeResolved('light');
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = (): void => setThemeResolved(mq.matches ? 'dark' : 'light');
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [preference]);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Tailwind の `dark:` バリアントも効かせる。判定は `LIGHT_THEMES` 経由。
    document.documentElement.classList.toggle('dark', !isLightTheme(theme));
    try {
      // 次回の初回描画で theme-init.js が読む値。**必ず解決済みを書く。**
      window.localStorage.setItem(storageKey, theme);
    } catch {
      /* プライベートモード等で localStorage が使えなくても致命的ではない */
    }
  }, [theme, storageKey]);

  const setTheme = React.useCallback(
    (next: ThemePreference) => {
      setPreferenceState(next);
      try {
        window.localStorage.setItem(prefKey, next);
      } catch {
        /* プライベートモード等で localStorage が使えなくても致命的ではない */
      }
    },
    [prefKey],
  );

  const value = React.useMemo(
    () => ({ theme, preference, preferences: themes, setTheme }),
    [theme, preference, themes, setTheme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme は <ThemeProvider> の内側でのみ使えます');
  return ctx;
}
